<template>
  <v-sheet elevation="2" class="inventory-root pa-2">

    <!-- TOP BAR -->
    <div class="inv-topbar">
      <div class="inv-topbar__brand">
        <span class="inv-topbar__bracket">[</span>
        <span class="inv-topbar__title">Machines & Tools</span>
        <span class="inv-topbar__bracket">]</span>
      </div>
        <div class="inv-topbar__meta">
          <span class="inv-topbar__counter">
            <span class="inv-topbar__counter-num">{{ filteredItems.length }}</span>
            <span class="inv-topbar__counter-label">/ {{ items.length }} Records</span>
          </span>
        
          <button class="inv-btn inv-btn--ghost" @click="exportExcel" title="Export Excel">
            <span class="inv-btn__icon">⬇</span> EXCEL
          </button>
          <button class="inv-btn inv-btn--ghost" @click="openPrintDialog" title="Nyomtatás">
            <span class="inv-btn__icon">⎙</span> PRINT
          </button>
        
          <button class="inv-btn inv-btn--primary" @click="openCreateDialog">
            <span class="inv-btn__icon">+</span> NEW TOOL
          </button>
        </div>
    </div>

    <!-- FILTER BAR -->
    <ToolsFilterBar
      :search.sync="search"
      :filter-type.sync="filterType"
      :filter-status.sync="filterStatus"
      :type-options="typeOptions"
      :status-options="statusOptions"
    />

    <!-- TABLE -->
    <ToolsTable
      :items="paginatedItems"
      :loading="loading"
      :visible-headers="visibleHeaders"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :current-page.sync="currentPage"
      :per-page.sync="perPage"
      :total-pages="totalPages"
      :page-range="pageRange"
      :type-options="typeOptions"
      :status-options="statusOptions"
      @sort="toggleSort"
      @view="viewItem"
      @edit="editItem"
      @delete="confirmDelete"
    />

    <!-- DIALOGS -->
    <v-fade-transition>
      <ToolsFormDialog
        v-model="formDialog"
        :edit-mode="editMode"
        :form="form"
        :type-options="typeOptions"
        :status-options="statusOptions"
        :taken-user-ids="taken_user_ids"
        @submit="saveItem"
      />
    </v-fade-transition>


    <v-fade-transition>
      <ToolsViewDialog
        v-model="viewDialog"
        :item="selectedItem"
        :type-options="typeOptions"
        :status-options="statusOptions"
        @edit="editFromView"
      />
    </v-fade-transition>

    <v-fade-transition>
      <ToolsDeleteDialog
        v-model="deleteDialog"
        :item="selectedItem"
        @confirm="deleteItem"
      />
    </v-fade-transition>

    <v-fade-transition>
      <div v-if="printDialog" class="inv-overlay" @click.self="printDialog = !printDialog">
        <div class="inv-modal inv-modal--print">
          <div class="inv-modal__header">
            <span>⎙ PRINT PREVIEW</span>
            <div style="display:flex; gap:8px;">
              <button class="inv-btn inv-btn--primary" @click="printNow">PRINT</button>
              <button class="inv-modal__close" @click="printDialog = false">×</button>
            </div>
          </div>
          <div class="inv-modal__body inv-modal__body--print">
            <ToolsPrintView
              ref="printView"
              :items="filteredItems"
              :type-options="typeOptions"
              :status-options="statusOptions"
              :filter-label="currentFilterLabel"
            />
          </div>
        </div>
      </div>
    </v-fade-transition>
  </v-sheet>
</template>

<script>
import ApiService from "@/core/services/api.service"
import ToolsFilterBar    from "./components/ToolsFilterBar.vue"
import ToolsTable        from "./components/ToolsTable.vue"
import ToolsFormDialog   from "./components/ToolsFormDialog.vue"
import ToolsViewDialog   from "./components/ToolsViewDialog.vue"
import ToolsDeleteDialog from "./components/ToolsDeleteDialog.vue"
import ToolsPrintView from "./components/ToolsPrintView.vue"
import ToolsExportMixin  from "@/view/mixins/ToolsExportMixin.js";
import { typeName, statusName, formatDate, typeHex, typeGlyph } from '../tools/utils/tools.utils'



export const ENDPOINT = "humanResources/tools/"

export const initialFormData = () => ({
  name: '',
  serial_number: '',
  type: 1,
  status: 1,
  model: '',
  purchase_date: null,
  warranty_expires_at: null,
  touch_protection_date: null,
  notes: '',
  attachments: [],
  meta: null,
  user_id: null,
})

export default {
  name: 'Tools',
  components: { ToolsFilterBar, ToolsTable, ToolsFormDialog, ToolsViewDialog, ToolsDeleteDialog, ToolsPrintView },
  mixins: [ToolsExportMixin],

  data() {
    return {
      loading: false,
      search: '',
      filterType: null,
      filterStatus: 1,
      sortBy: 'id',
      sortDesc: true,
      currentPage: 1,
      perPage: 10,

      deleteDialog: false,
      formDialog: false,
      viewDialog: false,
      editMode: false,
      selectedItem: null,

      form: initialFormData(),

      visibleHeaders: [
        { text: 'NAME',               value: 'name' },
        { text: 'SERIAL NUMBER',      value: 'serial_number' },
        { text: 'TYPE',               value: 'type' },
        { text: 'STATUS',             value: 'status' },
        { text: 'PURCHASE DATE',      value: 'purchase_date' },
        { text: 'WARRANTY EXPIRES AT',value: 'warranty_expires_at' },
        { text: 'TOUCH PROTECTION DATE',value: 'touch_protection_date' },
        { text: 'ATTACHED TO',value: 'user' },
        { text: 'NOTES',              value: 'notes', sortable: false },
      ],

      typeOptions: [
        { text: 'All', value: null },
        { text: 'Hand Tool', value: 1 },
        { text: 'Machine',   value: 2 },
      ],
      statusOptions: [
        { text: 'All', value: null },
        { text: 'Working',   value: 1 },
        { text: 'Not Working', value: 2 },
        { text: 'Archived', value: 3 },
        { text: 'Deleted', value: 4 },
      ],

      items: [],
      taken_user_ids: [],
      printDialog: false,
    }
  },

  created() {
    this.fetchTools()
  },

  computed: {
    filteredItems() {
      const q = (this.search || '').toLowerCase()
      return this.items
        .filter(item => {
          if (q && ![item.name, item.serial_number, item.model].some(v => v && v.toLowerCase().includes(q))) return false
          if (this.filterStatus && item.status !== this.filterStatus) return false
          if (this.filterType   && item.type   !== this.filterType)   return false

          return true
        })
        .sort((a, b) => {
          let va = a[this.sortBy], vb = b[this.sortBy]
          if (va == null) return 1; if (vb == null) return -1
          if (va < vb) return this.sortDesc ? 1 : -1
          if (va > vb) return this.sortDesc ? -1 : 1
          return 0
        })
    },
    totalPages()    { return Math.max(1, Math.ceil(this.filteredItems.length / this.perPage)) },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredItems.slice(start, start + this.perPage)
    },
    pageRange() {
      const total = this.totalPages, cur = this.currentPage
      const pages = []
      for (let i = Math.max(1, cur - 2); i <= Math.min(total, cur + 2); i++) pages.push(i)
      return pages
    },
  },

  watch: {
    filteredItems() { this.currentPage = 1 },
  },

  methods: {
    typeName, statusName, formatDate, typeHex, typeGlyph,
    toggleSort(col) {
      if (this.sortBy === col) this.sortDesc = !this.sortDesc
      else { this.sortBy = col; this.sortDesc = false }
    },

    openCreateDialog() {
      this.editMode = false
      this.form = initialFormData()
      this.formDialog = true
    },
    editItem(item) {
      this.editMode = true
      this.form = { ...item }
      this.selectedItem = item
      this.formDialog = true
    },
    editFromView() {
      this.viewDialog = false
      this.editItem(this.selectedItem)
    },
    viewItem(item)   { this.selectedItem = item; this.viewDialog = true },
    confirmDelete(i) { this.selectedItem = i; this.deleteDialog = true },

    saveItem(formData) {
      const fd = new FormData()
    
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'attachments') return
        if ((key === 'purchase_date' || key === 'warranty_expires_at' || key === 'touch_protection_date') && value)
          value = new Date(value).toISOString().slice(0, 10)
        if (key === 'meta' && value && typeof value === 'object') {
          fd.append('meta', JSON.stringify(value)); return
        }
        if (value !== null && value !== undefined) fd.append(key, value)
      })
    
      if (this.editMode) {
        fd.append('_method', 'PUT')
        ApiService.post(ENDPOINT + formData.id, fd)
          .then(() => {
            /*const idx = this.items.findIndex(i => i.id === formData.id)
            if (idx > -1) {

              this.$set(this.items, idx, { 
                ...formData, 
                attachments: this.items[idx].attachments
              })
            }
            Object.assign(this.selectedItem, formData)*/

            this.fetchTools();

            Object.assign(this.selectedItem, formData)
          
            const files = (formData.attachments || []).filter(f => f?.raw && f.status === 'pending')
            if (files.length) {
              this.uploadAttachments(formData.id, files).then(hasError => {
                if (!hasError) this.formDialog = false
              })
            } else {
              this.formDialog = false
            }
          })
          .catch(err => console.error('Failed to update tool:', err))
        
      } else {
        ApiService.post(ENDPOINT, fd)
          .then(res => {
            const newTool = res.data
            const newId = newTool.id
            this.items.push(newTool)
            this.currentPage = this.totalPages
          
            const files = (formData.attachments || []).filter(f => f?.raw && f.status === 'pending')
            if (files.length) {
              this.uploadAttachments(newId, files).then(hasError => {
                if (!hasError) this.formDialog = false
              })
            } else {
              this.formDialog = false
            }
          })
          .catch(err => console.error('Failed to create tool:', err))
      }
    },

    uploadAttachments(toolId, files) {
      files.forEach(f => { f.status = 'uploading'; f.progress = 0 })
      let hasError = false;
    
      const promises = files.map(file => {
        const fd = new FormData()
        fd.append('tool_id', toolId)
        fd.append('attachments', file.raw, file.name)
      
        return ApiService.post(`${ENDPOINT}${toolId}/attachments/upload`, fd, {
          onUploadProgress: e => {
            file.progress = Math.round((e.loaded / e.total) * 100)
          }
        })
        .then(res => { 
          file.status = 'done';
          const idx = this.items.findIndex(i => i.id === toolId)
          if (idx > -1) {
            const attachments = this.items[idx].attachments || []
            this.$set(this.items[idx], 'attachments', [...attachments, res.data])
          }
        })
        .catch((err) => { 
          console.log('ERROR', err)
          console.log('ERROR response', err?.response)
          console.log('ERROR status', err?.response?.status)
          file.status = 'error'; 
          hasError = true;

          const msg = err?.response?.data?.errors?.attachments?.[0] || err?.response?.data?.message || "Unknown error";

          file.errorMessage = msg;
        })
      })

      return Promise.all(promises).then(() => hasError)
    },

    deleteItem() {
      ApiService.delete(ENDPOINT + this.selectedItem.id)
        .then(() => {
          const idx = this.items.findIndex(i => i.id === this.selectedItem.id)
          if (idx > -1) {
            this.$set(this.items[idx], 'deleted_at', new Date().toISOString())
            this.$set(this.items[idx], 'status', 4) 
          }
        })
        .catch(err => console.error("Failed to delete tool:", err))
        .finally(() => { this.deleteDialog = false })
    },

    async fetchToolAttachments(toolId) {
      try {
        const res = await ApiService.get(`${ENDPOINT}${toolId}/attachments/get`)
        if (res.data) {
          this.$set(this.selectedItem, 'attachments', res.data)
          this.$set(this.form, 'attachments', res.data)
        }
      } catch (err) {
        console.error("Failed to fetch tool attachments:", err)
      }

    },

    async fetchTools() {
      this.loading = true
      await ApiService.get(ENDPOINT)
        .then(res => { this.taken_user_ids = res.data.taken_user_ids; this.items = res.data.data })
        .catch(err => console.error("Failed to fetch tools:", err))
        .finally(() => { this.loading = false })
    },
  },
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');

.inventory-root {
  --bg:          #f5f6f8;
  --bg2:         #ffffff;
  --bg3:         #f0f1f4;
  --border:      #e2e4ea;
  --border2:     #c8ccd6;
  --text:        #374151;
  --text-dim:    #9ca3af;
  --text-bright: #111827;
  --accent:      #2563eb;
  --accent2:     #1d4ed8;
  --danger:      #ef4444;
  --warn:        #f59e0b;
  --success:     #16a34a;

  background: var(--bg);
  color: var(--text);
  font-family: 'IBM Plex Sans', sans-serif;
}

/* Shared button styles (used by child modals via CSS vars) */
.inv-topbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 28px; border-bottom: 1px solid var(--border); background: var(--bg2);
}
.inv-topbar__brand {
  display: flex; align-items: center; gap: 6px;
  font-family: 'IBM Plex Mono', monospace; font-weight: 700; font-size: 17px;
  letter-spacing: 0.15em; color: var(--text-bright);
}
.inv-topbar__bracket { color: var(--accent); font-size: 21px; }
.inv-topbar__meta    { display: flex; align-items: center; gap: 20px; }
.inv-topbar__counter { font-family: 'IBM Plex Mono', monospace; font-size: 15px; }
.inv-topbar__counter-num   { color: var(--accent2); font-weight: 700; font-size: 19px; margin-right: 2px; }
.inv-topbar__counter-label { color: var(--text-dim); }

.inv-btn {
  font-family: 'IBM Plex Mono', monospace; font-size: 14px; font-weight: 600;
  letter-spacing: 0.1em; padding: 8px 18px; border: 1px solid; cursor: pointer;
  transition: all 0.15s; display: inline-flex; align-items: center; gap: 6px;
}
.inv-btn--primary { background: var(--accent); border-color: var(--accent); color: #fff; }
.inv-btn--primary:hover { background: var(--accent2); border-color: var(--accent2); }
.inv-btn__icon { font-size: 19px; line-height: 1; }

@media (min-width: 960px) and (max-width: 1263px) {
  .container {
    max-width: 970px !important;
  }
}

@media (min-width: 1264px) and (max-width: 1903px) {
  .container {
    max-width: 1555px !important;
  }
}

@media (min-width: 1904px) {
  .container {
    max-width: 2245px !important;
  }
}

/* ── RESPONSIVE ── */
@media (max-width: 768px) {
  .inv-topbar {
    padding: 12px 16px;
    flex-wrap: wrap;
    gap: 10px;
  }
  .inv-topbar__brand { font-size: 14px; }
  .inv-topbar__bracket { font-size: 18px; }
  .inv-topbar__counter-num { font-size: 16px; }
  .inv-topbar__counter { font-size: 13px; }
  .inv-btn { font-size: 12px; padding: 7px 14px; }
}

@media (max-width: 480px) {
  .inv-topbar { padding: 10px 12px; }
  .inv-topbar__brand { font-size: 12px; letter-spacing: 0.08em; }
  .inv-topbar__meta { width: 100%; justify-content: space-between; }
}

.inv-btn--ghost { background: transparent; border-color: var(--border2); color: var(--text); }
.inv-btn--ghost:hover { border-color: var(--text-dim); }

/* ── PRINT MODAL ── */
.inv-overlay {
  position: fixed; inset: 0; z-index: 999;
  background: rgba(0,0,0,0.35);
  display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(2px);
}
.inv-modal {
  background: var(--bg2); border: 1px solid var(--border2);
  box-shadow: 0 24px 64px rgba(0,0,0,0.15);
  display: flex; flex-direction: column; max-height: 90vh; overflow: hidden;
}
.inv-modal__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid var(--border);
  font-family: 'IBM Plex Mono', monospace; font-size: 16px; font-weight: 700;
  letter-spacing: 0.08em; color: var(--text-bright); background: var(--bg3);
}
.inv-modal__close {
  background: none; border: none; color: var(--text-dim);
  font-size: 23px; cursor: pointer; line-height: 1; padding: 0 4px;
}
.inv-modal__close:hover { color: var(--text); }
.inv-modal__footer {
  padding: 14px 20px; border-top: 1px solid var(--border);
  display: flex; justify-content: flex-end; gap: 10px; background: var(--bg3);
}
.inv-modal--print {
  width: 90vw;
  max-width: 1100px;
  max-height: 90vh;
}
.inv-modal__body--print {
  background: #f5f6f8;
  padding: 16px;
  overflow-y: auto;
}
</style>